import { DateTime } from 'luxon';
import { BADGE_ONLINE_MINUTES } from './badgeTimes';

/**
 * One node on the mesh, as GET /network reports it: a gateway, an M2/M3
 * anchor, a hub or a badge.
 *
 * Read-only on the client — the server builds these from diagnostics — so
 * there is no freeze, only thaw and getters.
 */
export default class NetworkNode {
  static KINDS = {
    GATEWAY: 'Gateway',
    ANCHOR: 'Anchor',
    HUB: 'Hub',
    BADGE: 'Badge',
    UNKNOWN: 'Unknown'
  };

  /** Link quality bands, as the design draws them. Lower bound inclusive. */
  static QUALITY_BANDS = [
    { key: 'strong', label: 'Strong', min: 90 },
    { key: 'good', label: 'Good', min: 75 },
    { key: 'marginal', label: 'Marginal', min: 65 },
    { key: 'weak', label: 'Weak', min: -Infinity }
  ];

  /** Minutes without a report before a node counts as off the mesh. Matches Hub. */
  static ONLINE_MINUTES = 20;

  static thaw(obj) {
    if (obj == null) return null;
    const node = new NetworkNode();
    node.nodeAddress = obj.nodeAddress ?? null;
    node.networkAddress = obj.networkAddress ?? null;
    node.kind = obj.kind ?? NetworkNode.KINDS.UNKNOWN;
    node.name = obj.name ?? null;
    node.label = obj.label ?? null;
    node.serialNumber = obj.serialNumber ?? null;
    node.role = obj.role ?? null;
    node.model = obj.model ?? null;
    node.appVersion = obj.appVersion ?? null;
    node.sinkAddress = obj.sinkAddress ?? null;
    node.hopCount = obj.hopCount ?? null;
    node.nextHop = obj.nextHop ?? null;
    node.nextHopQuality = obj.nextHopQuality ?? null;
    node.nextHopRssi = obj.nextHopRssi ?? null;
    node.nextHopPower = obj.nextHopPower ?? null;
    node.voltage = obj.voltage ?? null;
    node.packetErrorRate = obj.packetErrorRate ?? null;
    node.installationQuality = obj.installationQuality ?? null;
    node.neighborCount = obj.neighborCount ?? null;
    node.neighbors = obj.neighbors ?? [];
    node.travelTimeMs = obj.travelTimeMs ?? null;
    node.lastSeenTimestamp = obj.lastSeenTimestamp ? DateTime.fromISO(obj.lastSeenTimestamp) : null;
    node.lastDiagnosticTimestamp = obj.lastDiagnosticTimestamp ? DateTime.fromISO(obj.lastDiagnosticTimestamp) : null;
    return node;
  }

  static thawList(list) {
    return (list ?? []).map((obj) => NetworkNode.thaw(obj));
  }

  /** The band a quality percentage falls in, or null when there is none. */
  static qualityBand(quality) {
    if (quality == null) return null;
    return NetworkNode.QUALITY_BANDS.find((band) => quality >= band.min);
  }

  getNodeAddress() {
    return this.nodeAddress;
  }

  getKind() {
    return this.kind;
  }

  isGateway() {
    return this.kind === NetworkNode.KINDS.GATEWAY;
  }

  /** The name a Super user gave it, or what it is attached to. */
  getLabel() {
    return this.label;
  }

  getName() {
    return this.name;
  }

  getSerialNumber() {
    return this.serialNumber;
  }

  /**
   * The short role the design puts in a column: GW, M2/M3 for anchors, and
   * Hub or Badge for SimulAlert devices.
   */
  getRoleLabel() {
    switch (this.kind) {
      case NetworkNode.KINDS.GATEWAY: return 'GW';
      case NetworkNode.KINDS.ANCHOR: return this.model ?? 'Anchor';
      case NetworkNode.KINDS.HUB: return 'Hub';
      case NetworkNode.KINDS.BADGE: return 'Badge';
      default: return '—';
    }
  }

  getModel() {
    return this.model;
  }

  getAppVersion() {
    return this.appVersion;
  }

  /** The gateway this node drains to. */
  getSinkAddress() {
    return this.sinkAddress;
  }

  /** Gateways sit at tier 0 whatever they report about themselves. */
  getHopCount() {
    return this.isGateway() ? 0 : this.hopCount;
  }

  getNextHop() {
    return this.isGateway() ? null : this.nextHop;
  }

  getNextHopQuality() {
    return this.nextHopQuality;
  }

  /** 'strong', 'good', 'marginal' or 'weak'; null when not reported. */
  getQualityBand() {
    return NetworkNode.qualityBand(this.nextHopQuality)?.key ?? null;
  }

  getNextHopRssi() {
    return this.nextHopRssi;
  }

  getVoltage() {
    return this.voltage;
  }

  getPacketErrorRate() {
    return this.packetErrorRate;
  }

  getNeighborCount() {
    return this.neighborCount;
  }

  getNeighbors() {
    return this.neighbors;
  }

  /** Device clock to server, from the latest report that carried it. */
  getLatencyMs() {
    return this.travelTimeMs;
  }

  getLastSeenTimestamp() {
    return this.lastSeenTimestamp;
  }

  /**
   * On the mesh (true), off it (false), or never reported (null).
   *
   * The node list includes devices the platform knows but has not heard from
   * in weeks, and it only timestamps gateways. A node with no report at all is
   * not evidence of anything either way, so it is not called offline.
   */
  getIsConnected() {
    if (this.lastSeenTimestamp == null) return null;
    const minutes = Math.abs(this.lastSeenTimestamp.diffNow('minutes').minutes);
    // A badge keeps the badge's own limit, so it cannot read Connected on
    // Staff and off the mesh here.
    return minutes < (this.kind === NetworkNode.KINDS.BADGE ? BADGE_ONLINE_MINUTES : NetworkNode.ONLINE_MINUTES);
  }

  /**
   * Battery charge in percent, on the same scale the server uses for each
   * device type. Gateways are mains powered and report 0 V.
   */
  getBatteryPercent() {
    if (this.voltage == null || this.voltage <= 0 || this.isGateway()) return null;
    // Badges: 3.1 V empty, 4.1 V full. Hubs and anchors: 3.0 V empty, 3.5 V full.
    const [empty, span] = this.kind === NetworkNode.KINDS.BADGE ? [3.1, 1.0] : [3.0, 0.5];
    return Math.min(Math.max(Math.round((this.voltage - empty) * 100 / span), 0), 100);
  }
}
